import React, { useState } from 'react';
import { ThemeProvider } from '@mui/material';
import { useThemeMode } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Paper,
  Container,
  CircularProgress
} from '@mui/material';
import { supabase } from '../supabaseClient';

export default function ResetPasswordConfirmPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError("Şifreler eşleşmiyor");
      return;
    }

    try {
      setLoading(true);
      setError('');

      const { data, error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      navigate('/login', { 
        state: { 
          message: 'Şifreniz başarıyla sıfırlandı. Lütfen yeni şifrenizle giriş yapın.',
          severity: 'success'
        }
      });
    } catch (error) {
      setError('Şifre sıfırlama işlemi sırasında bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const { theme } = useThemeMode();

  return (
    <ThemeProvider theme={theme}>
    <Box sx={{
      minHeight: '100vh',
      width: '100vw',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      background: theme => theme.palette.mode === 'dark' 
        ? '#1A1A2A' 
        : '#f5f7fa',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        background: theme => theme.palette.mode === 'dark'
          ? `radial-gradient(circle at center, rgba(0, 74, 173, 0.15) 0%, transparent 50%),
             repeating-linear-gradient(45deg, rgba(0, 74, 173, 0.08) 0px, rgba(0, 74, 173, 0.08) 2px, transparent 2px, transparent 10px),
             repeating-linear-gradient(-45deg, rgba(0, 74, 173, 0.08) 0px, rgba(0, 74, 173, 0.08) 2px, transparent 2px, transparent 10px)`
          : `radial-gradient(circle at center, rgba(0, 74, 173, 0.08) 0%, transparent 50%),
             repeating-linear-gradient(45deg, rgba(0, 74, 173, 0.03) 0px, rgba(0, 74, 173, 0.03) 2px, transparent 2px, transparent 10px),
             repeating-linear-gradient(-45deg, rgba(0, 74, 173, 0.03) 0px, rgba(0, 74, 173, 0.03) 2px, transparent 2px, transparent 10px)`,
        backgroundSize: '100% 100%, 20px 20px, 20px 20px',
        animation: 'gradientMove 20s ease-in-out infinite',
        zIndex: 0,
      },
      '@keyframes gradientMove': {
        '0%, 100%': { 
          backgroundPosition: '0% 0%, 0px 0px, 0px 0px',
          opacity: 0.8
        },
        '50%': { 
          backgroundPosition: '0% 0%, 10px 10px, -10px -10px',
          opacity: 1
        }
      }
    }}>
      <Container maxWidth="xs" sx={{
        width: '100%',
        maxWidth: { xs: '100%', sm: '440px' },
        mx: 'auto',
        px: { xs: 1, sm: 2 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh'
      }}>
        <Paper
          elevation={24}
          sx={{
            padding: { xs: 2, sm: 3 },
            margin: { xs: 1, sm: 2 },
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            borderRadius: '24px',
            backgroundColor: theme => theme.palette.background.paper,
            backdropFilter: 'blur(10px)',
            border: theme => `1px solid ${theme.palette.divider}`,
            boxShadow: theme => `0 4px 24px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
          }}
        >
          <Typography 
            component="h1" 
            variant="h5" 
            sx={{ 
              mb: 3,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              color: 'primary.main',
              fontWeight: 600
            }}>
            Yeni Şifre Belirle
          </Typography>

          <form onSubmit={handlePasswordReset} style={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Yeni Şifre"
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'background.paper',
                },
                '& .MuiOutlinedInput-input': {
                  padding: '14px 16px',
                }
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Yeni Şifre (Tekrar)"
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'background.paper',
                },
                '& .MuiOutlinedInput-input': {
                  padding: '14px 16px',
                }
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2,
                py: 1.5,
                borderRadius: '8px',
                fontWeight: 600
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Şifreyi Sıfırla'}
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
    </ThemeProvider>
  );
}
